import _ from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {computeIsCreateContactFormFieldErred} from '../../../../../../../src/helpers/createContactFormHelpers';
import {buildContactForHandle, ContactSourcesEnum} from '../../../../../../../src/models/api/contactModel';
import {CreateCardTemplate} from '../../../../../../../src/store/data/cards/cardsActions';
import {ContactsManagerCreateContactFormState} from '../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerCreateContactFormState';
import {CreateContactFormCreateContactAsyncHandler} from '../../../../../../../src/store/panels/contacts/createContactForm/createContactFormActions';
import {
  CreateContactFormFieldModel,
  CreateContactFormFieldTypesEnum,
  CreateContactFormFieldValidityStatusesEnum,
} from '../../../../../../../src/store/panels/contacts/createContactForm/createContactFormState';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {
  buildContactsManagerCreateContactEvent,
  ContactsManagerTrackEventHandler,
} from '../helpers/trackingHelpers';

/*
 * Props.
 */

interface CreateContactFormProps {
  panelId: ContactsManagerPanelId;
  createContactFormState: ContactsManagerCreateContactFormState;
  closeSidePanel: Handler;
  createCardAsync: CreateContactFormCreateContactAsyncHandler;
  onTrackEvent: ContactsManagerTrackEventHandler;
}

/*
 * Style.
 */

const StyledCreateContactFooterDiv = styled.div`
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  display: flex;
  flex-direction: row-reverse;
  gap: 10px;
`;

/*
 * Component.
 */

export const CreateContactFooter: FC<CreateContactFormProps> = (props) => {
  const {panelId, createContactFormState, closeSidePanel, createCardAsync, onTrackEvent} = props;

  const shouldDisableCreate = useMemo(() => {
    // Check for an invalid field.
    const hasInvalidField = createContactFormState.contactFields.some((field) =>
      computeIsCreateContactFormFieldErred(Boolean(field.value), field.validity),
    );
    // Check for any valid field, we need to have at least 1 valid field to be able to create.
    const hasValidField = createContactFormState.contactFields.some(
      (field) => field.validity === CreateContactFormFieldValidityStatusesEnum.VALID,
    );
    return hasInvalidField || !hasValidField;
  }, [createContactFormState.contactFields]);
  const isSaving = createContactFormState.createStatus === AsyncStatusesEnum.LOADING;

  return (
    <StyledCreateContactFooterDiv>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={shouldDisableCreate || isSaving}
        onClick={() => onCreateAsync(panelId, createContactFormState, createCardAsync, onTrackEvent)}
      >
        {renderCreateButtonText(isSaving)}
      </Button>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={closeSidePanel}>
        <Trans id="contacts-manager-contacts-create-form-cancel">Cancel</Trans>
      </Button>
    </StyledCreateContactFooterDiv>
  );
};

/*
 * Helpers.
 */

async function onCreateAsync(
  panelId: ContactsManagerPanelId,
  formState: ContactsManagerCreateContactFormState,
  createCardAsync: CreateContactFormCreateContactAsyncHandler,
  onTrackEvent: ContactsManagerTrackEventHandler,
) {
  const template: CreateCardTemplate = {
    namespace: formState.access,
    name: formState.name,
    contacts: _(formState.contactFields)
      .filter((field) => field.value.trim() !== '')
      .map(buildContactFieldContact)
      .compact()
      .value(),
    customFieldAttributes: _(formState.customAttributes)
      .filter((attr) => Boolean(attr.value.trim()))
      .value(),
    accountId: formState.accountId,
    links: _(formState.links)
      .map((link) => link.value)
      .filter(Boolean)
      .value(),
    cardGroupIds: formState.cardGroupIds,
    bio: formState.description,
  };
  await createCardAsync(panelId, template, formState.avatarFile);
  onTrackEvent(
    buildContactsManagerCreateContactEvent(
      formState.access,
      Boolean(formState.accountId),
      Boolean(formState.cardGroupIds.length),
    ),
  );
}

function buildContactFieldContact(field: CreateContactFormFieldModel) {
  if (field.type === CreateContactFormFieldTypesEnum.EMAIL) {
    return buildContactForHandle(ContactSourcesEnum.EMAIL, field.value);
  }
  if (field.type === CreateContactFormFieldTypesEnum.PHONE_NUMBER) {
    return buildContactForHandle(ContactSourcesEnum.PHONE, field.value);
  }
  if (field.type === CreateContactFormFieldTypesEnum.TWITTER) {
    return buildContactForHandle(ContactSourcesEnum.TWITTER, field.value);
  }
  return undefined;
}

function renderCreateButtonText(isSaving: boolean) {
  if (isSaving) {
    return <Trans id="contacts-manager-contacts-create-form-creating">Creating...</Trans>;
  }

  return <Trans id="contacts-manager-contacts-create-form-create">Create</Trans>;
}
